import { useCallback, useEffect, useRef, useState } from 'react';

export interface RemoteState<T> {
  status: 'loading' | 'ready' | 'error';
  /** The last good answer — kept while a reload is in flight. */
  data: T | null;
  error: unknown;
  reload: () => void;
}

/**
 * Loads `load()` on mount and whenever its identity changes (callers memoize it on their query);
 * an answer for an older call is dropped. `reload()` refetches without blanking `data`.
 */
export function useRemote<T>(load: () => Promise<T>): RemoteState<T> {
  const [status, setStatus] = useState<RemoteState<T>['status']>('loading');
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const seq = useRef(0);

  const reload = useCallback(() => {
    seq.current += 1;
    const mine = seq.current;
    setStatus('loading');
    setError(null);
    load().then(
      (next) => {
        if (seq.current !== mine) return;
        setData(next);
        setStatus('ready');
      },
      (err: unknown) => {
        if (seq.current !== mine) return;
        setError(err);
        setStatus('error');
      },
    );
  }, [load]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { status, data, error, reload };
}
